// ROOM 404: Member Briefing (Daily Command Center)

import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  Compass,
  Lightbulb,
  MessageSquare,
  Shield,
  ShieldAlert,
  Sparkles,
  Target,
  Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/api';
import type { CommunityIdea, EventItem, Mission, Room } from '../types';

interface BriefingProps {
  onNavigateTab: (tab: 'rooms' | 'ideas' | 'missions' | 'identity') => void;
  onOpenRoom: (roomId: string) => void;
  onOpenEvents: () => void;
  onOpenReport: () => void;
}

export const Briefing: React.FC<BriefingProps> = ({
  onNavigateTab,
  onOpenRoom,
  onOpenEvents,
  onOpenReport,
}) => {
  const { session } = useAuth();
  const [activeRooms, setActiveRooms] = useState<Room[]>([]);
  const [featuredIdea, setFeaturedIdea] = useState<CommunityIdea | null>(null);
  const [currentMission, setCurrentMission] = useState<Mission | null>(null);
  const [nextEvent, setNextEvent] = useState<EventItem | null>(null);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBriefingData = async () => {
      try {
        const [roomsRes, ideasRes, missionsRes, eventsRes, announcementsRes] = await Promise.allSettled([
          apiRequest<{ rooms: Room[] }>('/api/rooms'),
          apiRequest<{ ideas: CommunityIdea[] }>('/api/ideas'),
          apiRequest<{ missions: Mission[] }>('/api/missions'),
          apiRequest<{ events: EventItem[] }>('/api/events'),
          apiRequest<{ announcements: any[] }>('/api/announcements'),
        ]);

        if (roomsRes.status === 'fulfilled') {
          setActiveRooms(roomsRes.value.rooms.slice(0, 4));
        }
        if (ideasRes.status === 'fulfilled' && ideasRes.value.ideas.length > 0) {
          setFeaturedIdea(ideasRes.value.ideas[0]);
        }
        if (missionsRes.status === 'fulfilled' && missionsRes.value.missions.length > 0) {
          setCurrentMission(missionsRes.value.missions[0]);
        }
        if (eventsRes.status === 'fulfilled' && eventsRes.value.events.length > 0) {
          setNextEvent(eventsRes.value.events[0]);
        }
        if (announcementsRes.status === 'fulfilled') {
          setAnnouncements(announcementsRes.value.announcements || []);
        }
      } finally {
        setLoading(false);
      }
    };

    loadBriefingData();
  }, []);

  const standing = session?.profile?.standing || (session?.status === 'restricted' ? 'restricted' : 'good');
  const isRestricted = standing === 'restricted' || session?.status === 'restricted';
  const isProbation = standing === 'probation';

  const latestAnnouncement = announcements.length > 0 ? announcements[0] : null;

  return (
    <div className="space-y-4 pb-20 max-w-xl mx-auto px-4 pt-3">
      {/* Welcome & Standing Header */}
      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-[#12141c] to-[#181b26] border border-[#1f2433] shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 font-bold font-mono text-sm">
              {session?.profile?.alias ? session.profile.alias.slice(0, 2).toUpperCase() : '404'}
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-bold text-white tracking-tight">
                {session?.profile?.alias || 'Initiate'}
              </h1>
              <div className="flex items-center space-x-1.5 text-xs text-gray-400 font-mono">
                <span>#{session?.profile?.memberNumber || '---'}</span>
                <span>•</span>
                <span className="text-emerald-400">{session?.profile?.cohort || 'Cohort 404'}</span>
              </div>
            </div>
          </div>

          <div className="text-right">
            <span
              className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                isRestricted
                  ? 'bg-rose-500/10 text-rose-400 border-rose-500/30'
                  : isProbation
                  ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                  : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              }`}
            >
              {isRestricted ? (
                <>
                  <ShieldAlert className="w-3.5 h-3.5" />
                  <span>Restricted Standing</span>
                </>
              ) : isProbation ? (
                <>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>Probation</span>
                </>
              ) : (
                <>
                  <Shield className="w-3.5 h-3.5" />
                  <span>Good Standing</span>
                </>
              )}
            </span>
          </div>
        </div>

        {/* Status Alert if Restricted */}
        {isRestricted && (
          <div className="mb-2.5 p-2.5 rounded-xl bg-rose-950/40 border border-rose-500/30 text-xs text-rose-300 leading-snug flex items-start space-x-2">
            <ShieldAlert className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold text-rose-200">Account Restricted:</span> Communication privileges are limited by moderation policy.
            </div>
          </div>
        )}

        {/* Philosophy Disclosure Banner */}
        <div className="bg-[#090a0f]/60 rounded-xl p-2.5 border border-[#1f2433] text-[11px] text-gray-400 leading-snug">
          <span className="text-emerald-400 font-semibold">Perimeter Principle:</span> Secure perimeter, relaxed interior. Chat freely, submit proposals without approval, and participate with your alias.
        </div>
      </div>

      {/* Society Announcement Banner */}
      <div
        onClick={() => onOpenRoom('official-announcements')}
        className="p-3.5 rounded-2xl bg-gradient-to-r from-emerald-950/40 to-[#12141c] border border-emerald-500/30 cursor-pointer active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center justify-between text-xs font-semibold text-emerald-400 mb-1">
          <span className="flex items-center space-x-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Official Announcement</span>
          </span>
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
        <h3 className="text-sm font-semibold text-white">
          {latestAnnouncement ? latestAnnouncement.body.split('\n')[0].replace(/^##\s*/, '') : 'Inaugural Cohort Admissions Open'}
        </h3>
        <p className="text-xs text-gray-400 mt-1 line-clamp-2">
          {latestAnnouncement ? latestAnnouncement.body.replace(/^##[^\n]*\n*/, '') : 'Admissions for the first 1,000 verified students are underway. Student e-IDs are permanently purged post-review. Welcome to the interior.'}
        </p>
      </div>

      {/* Current Mission Card */}
      {currentMission ? (
        <div
          onClick={() => onNavigateTab('missions')}
          className="p-3.5 rounded-2xl bg-[#12141c] border border-[#1f2433] cursor-pointer hover:border-gray-700 transition-colors"
        >
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5">
            <span className="flex items-center space-x-1.5 text-indigo-400 font-semibold">
              <Target className="w-3.5 h-3.5" />
              <span>Current Mission</span>
            </span>
            <span className="text-amber-400 font-mono text-[11px]">+{currentMission.points} pts</span>
          </div>
          <h4 className="text-sm font-semibold text-white">{currentMission.title}</h4>
          <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{currentMission.brief}</p>
        </div>
      ) : null}

      {/* Next Event RSVP */}
      {nextEvent ? (
        <div
          onClick={onOpenEvents}
          className="p-3.5 rounded-2xl bg-[#12141c] border border-[#1f2433] cursor-pointer hover:border-gray-700 transition-colors"
        >
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5">
            <span className="flex items-center space-x-1.5 text-cyan-400 font-semibold">
              <Calendar className="w-3.5 h-3.5" />
              <span>Upcoming Society Event</span>
            </span>
            <span className="text-[11px] text-gray-400 font-mono">
              {new Date(nextEvent.event_time).toLocaleDateString([], { month: 'short', day: 'numeric' })}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-white">{nextEvent.title}</h4>
          <div className="flex items-center justify-between mt-2 text-xs text-gray-400 font-mono">
            <span>{nextEvent.approximate_location}</span>
            <span className="text-emerald-400 font-semibold">RSVP Active</span>
          </div>
        </div>
      ) : null}

      {/* Active Community Idea */}
      {featuredIdea ? (
        <div
          onClick={() => onNavigateTab('ideas')}
          className="p-3.5 rounded-2xl bg-[#12141c] border border-[#1f2433] cursor-pointer hover:border-gray-700 transition-colors"
        >
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5">
            <span className="flex items-center space-x-1.5 text-amber-400 font-semibold">
              <Lightbulb className="w-3.5 h-3.5" />
              <span>Community Idea</span>
            </span>
            <span className="text-emerald-400 font-mono text-[11px]">
              {featuredIdea.interest_count} Interested
            </span>
          </div>
          <h4 className="text-sm font-semibold text-white">{featuredIdea.title}</h4>
          <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{featuredIdea.description}</p>
        </div>
      ) : null}

      {/* Recently Active Joined Rooms */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs font-semibold text-gray-300">
          <span className="flex items-center space-x-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
            <span>Active Rooms</span>
          </span>
          <button
            onClick={() => onNavigateTab('rooms')}
            className="text-emerald-400 hover:underline flex items-center space-x-0.5"
          >
            <span>View all</span>
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {activeRooms.map((room) => (
            <div
              key={room.id}
              onClick={() => onOpenRoom(room.id)}
              className="p-3 rounded-xl bg-[#12141c] border border-[#1f2433] hover:border-emerald-500/40 cursor-pointer transition-colors active:scale-95"
            >
              <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
                <span className="capitalize">{room.category}</span>
                <span className="flex items-center space-x-0.5">
                  <Users className="w-2.5 h-2.5" />
                  <span>{room.member_count}</span>
                </span>
              </div>
              <h5 className="text-xs font-semibold text-gray-100 truncate">{room.name}</h5>
            </div>
          ))}
        </div>
      </div>

      {/* Report & Safety Footer Shortcut */}
      <div className="pt-2">
        <button
          onClick={onOpenReport}
          className="w-full py-2.5 px-3 rounded-xl bg-[#12141c]/50 border border-[#1f2433] text-gray-400 hover:text-amber-400 hover:border-amber-500/30 text-xs font-medium flex items-center justify-center space-x-1.5 transition-colors"
        >
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
          <span>Safety & Confidential Reports</span>
        </button>
      </div>
    </div>
  );
};
